import { useNumberFormatter } from 'common/hooks/use-number-formatter';
import { useMemo } from 'react';
import type { ProductPriceToDisplayProps } from './types';
import { getCurrency } from 'utils/currency';

const MAX_PERCENT = 100;
type Type = 'default' | 'discount' | 'best';

export const useProductPrice = ({
  price,
  productPriceCategory,
  hasOldPrice,
}: ProductPriceToDisplayProps) => {
  const DEFAULT_CURRENCY = 'RUB';
  const currency = price.currency || DEFAULT_CURRENCY;

  const currencyFormatter = useNumberFormatter({
    options: {
      currency,
      currencyDisplay: 'narrowSymbol',
      style: 'currency',
    },
  });

  const formattedOldPrice = useMemo(
    () => currencyFormatter.format(price.previous_price || 0),
    [currencyFormatter, price.previous_price],
  );

  const finalCurrency = useMemo(() => {
    return getCurrency(currencyFormatter);
  }, [currencyFormatter]);

  const mainPrice = Number(price.main_price);

  const hasBestPrice = productPriceCategory === 'BPR';
  const hasSpecialPrice = hasOldPrice || hasBestPrice;

  let type: Type = 'default';

  if (hasOldPrice) {
    type = 'discount';
  }

  if (hasBestPrice) {
    type = 'best';
  }

  const uomRus = price.main_uom_rus ? `/${price.main_uom_rus}` : '';
  const additionalUomRus = price.additional_uom_rus ? `/${price.additional_uom_rus}` : '';
  const units = `${finalCurrency}${uomRus}`;
  const unitUnits = `${finalCurrency}${additionalUomRus}`;
  const discount = Math.trunc((price.discount_percent ?? 0) * MAX_PERCENT);

  return {
    discount,
    finalCurrency,
    mainPrice,
    formattedOldPrice,
    hasBestPrice,
    hasSpecialPrice,
    type,
    unitUnits,
    units,
  };
};
